<template>
  <div
    class="chart-actions"
    :class="{ highlighted: isHighlighted }">
    <div
      v-if="!isHighlighted && campaignManagedBudget && isDailyCapAvailable"
      class="group daily-cap">
      <div class="group-inner">
        <div class="label">
          {{ capitalize($t('messages.dailyCap')) }}
        </div>
        <div
          v-show="true"
          class="toggle">
          <toggle-button
            :value="shouldDailyCapToggleBeDisabled ? false : shouldShowDailyCap"
            :sync="true"
            :disabled="shouldDailyCapToggleBeDisabled"
            @change="toggleImpressionsAndBudget()" />
        </div>
      </div>
    </div>
    <div
      v-for="(action, i) in groupActions"
      :key="i"
      class="group">
      <div
        v-if="shouldShowAction(action)"
        class="group-inner">
        <div
          v-if="!isHighlighted"
          class="label">
          {{ action.label }}
        </div>
        <div :class="{ options: !isHighlighted }">
          <!-- IF GROUP ACTION IS CHART TYPE, USE ICONS-->
          <div v-if="action.type === ChartGroupActionType.CHART_TYPE">
            <tabs-slider
              icon-height="1.8rem"
              icon-width="1.8rem"
              :width="`${action.options.length * 4}rem`"
              height="3rem"
              tab-style="min-width: 4rem;"
              :show-handle-bars="false"
              :selected-tab-i-d="action.options.find((x) => x.isSelected).id"
              :tabs="
                action.options.map((option) => ({
                  id: option.id,
                  option,
                  name: getIconForChartType(option.id) == null ? option.label : null,
                  icon: getIconForChartType(option.id),
                }))
              "
              @selected-tab-changed="(x) => optionSelected(action, x.option)" />
          </div>
          <div v-if="action.type === ChartGroupActionType.BUTTONS">
            <tabs-slider
              height="3rem"
              width="8rem"
              :show-handle-bars="false"
              tab-style="min-width: 4rem;"
              :selected-tab-i-d="action.options.find((x) => x.isSelected).id"
              :tabs="action.options.map((option) => ({ id: option.id, option, name: option.label }))"
              @selected-tab-changed="(x) => optionSelected(action, x.option)" />
          </div>
          <div v-if="action.type === ChartGroupActionType.TIMEZONE">
            <chart-timezone-action
              :action="action"
              :align-to-right="isFullscreen"
              :local-cache-key="localCacheKey"
              @timezone-selected="(option) => optionSelected(action, option)" />
          </div>
          <div v-if="action.type === ChartGroupActionType.SELECTABLE">
            <!-- :key forces the component to refresh when another option is selected-->
            <chart-selectable-action
              :key="
                action && action.options && action.options.find((o) => o.isSelected)
                  ? action.options.find((o) => o.isSelected).id
                  : action.id
              "
              :action="action"
              @chart-option-selected="(option) => optionSelected(action, option)" />
          </div>
          <!-- CLICKABLE BUTTON ARE NOT SELECTABLE, THEY ARE ONLY CLICKABLE-->
          <div
            v-if="action.type === ChartGroupActionType.CLICKABLE"
            class="clickable">
            <button
              v-for="(option, j) in action.options"
              :key="j"
              class="btn btn-xs btn-default"
              @click="optionSelected(action, option)">
              <span>{{ option.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!isHighlighted"
      class="reset">
      <button
        class="btn btn-xs btn-default mt-1"
        :disabled="!canReset"
        :class="{ 'btn-no-text': isHighlighted }"
        @click="reset">
        <span>{{ $t('messages.chartGroupActionReset') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed } from 'vue';

  import ChartSelectableAction from '@/common/components/chart/ChartSelectableAction.vue';
  import ChartTimezoneAction from '@/common/components/chart/ChartTimezoneAction.vue';
  import TabsSlider from '@/common/components/controls/TabsSlider.vue';
  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import ChartEvents from '@/common/enums/chart-events.enum';
  import ChartGroupActionType from '@/common/enums/chart-group-action-type.enum';
  import ChartType from '@/common/enums/chart-type.enum';
  import {
    IconChartArea,
    IconChartBar,
    IconChartCandlestick,
    IconChartLine,
    IconChartPie,
  } from '@/common/icons/monochrome';
  import ChartGroupAction from '@/common/models/chart/ChartGroupAction';

  import IShouldDailyCapToggleBeAvailable from './IShouldDailyCapToggleBeAvailable';

  interface IProps {
    canReset: boolean;
    groupActions: ChartGroupAction[];
    shouldShowDailyCap?: boolean;
    shouldDailyCapToggleBeAvailable: IShouldDailyCapToggleBeAvailable | null;
    isHighlighted: boolean;
    isFullscreen: boolean;
    localCacheKey: string;
  }

  const { groupActions, shouldDailyCapToggleBeAvailable } = defineProps<IProps>();

  interface IEmits {
    (e: ChartEvents.GO_FULLSCREEN, value: boolean): void;
    (e: ChartEvents.OPTION_SELECTED, value: { action: ChartGroupAction; option: any }): void;
    (e: ChartEvents.RESET): void;
    (e: ChartEvents.SHOW_DAILY_CAP): void;
  }

  const emits = defineEmits<IEmits>();

  const campaignManagedBudget = computed(() => shouldDailyCapToggleBeAvailable?.campaignManagedBudget);
  const budgetModeMatchesActiveChart = computed(() => shouldDailyCapToggleBeAvailable?.budgetModeMatchesActiveChart);
  const isChartGranularitySetToDay = computed(() => shouldDailyCapToggleBeAvailable?.isChartGranularitySetToDay);
  const isDailyCapAvailable = computed(() => shouldDailyCapToggleBeAvailable?.isDailyCapAvailable);
  const shouldDailyCapToggleBeDisabled = computed(
    () => !isChartGranularitySetToDay.value || !budgetModeMatchesActiveChart.value,
  );

  function getIconForChartType(id: string) {
    switch (id) {
      case ChartType.AREA:
        return IconChartArea;
      case ChartType.BAR:
        return IconChartBar;
      case ChartType.CANDLESTICK:
        return IconChartCandlestick;
      case ChartType.LINE:
        return IconChartLine;
      case ChartType.PIE:
        return IconChartPie;
      default:
        return null;
    }
  }

  function shouldShowAction(action: ChartGroupAction) {
    // these actions below should have more than option, if they don't, then don't show them
    const actionWithOptions: string[] = [
      ChartGroupActionType.CHART_TYPE,
      ChartGroupActionType.SELECTABLE,
      ChartGroupActionType.TIMEZONE,
    ];
    if (actionWithOptions.includes(action.type)) {
      return true;
    }

    return action.options.length > 1;
  }

  function optionSelected(action: ChartGroupAction, option) {
    if (!action || !option) return;

    option.isSelected = true;
    action.options.forEach((o) => {
      o.isSelected = option.id === o.id;
    });
    emits(ChartEvents.OPTION_SELECTED, { action, option });
  }

  function reset() {
    emits(ChartEvents.RESET);
  }

  function toggleImpressionsAndBudget() {
    emits(ChartEvents.SHOW_DAILY_CAP);
  }
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  #app {
    .chart-actions {
      display: flex;
      flex-direction: row;

      &:not(.highlighted) {
        flex-direction: column;

        .group {
          display: flex;
          flex-direction: column;

          &:not(:last-of-type) {
            .group-inner {
              border-bottom: 0.1rem solid var(--defaultButtonBorderColor);
            }
          }

          .group-inner {
            display: flex;
            flex-direction: column;
            padding: 1.5rem 2rem;
          }

          .clickable {
            display: flex;

            button {
              cursor: pointer;

              &:not(:last-of-type) {
                margin-right: 0.5rem;
              }

              &:hover {
                background: var(--actionsColor);
                color: var(--actionsColorReverse);

                transition: all 500ms;
              }
            }
          }
        }
      }

      .reset {
        display: flex;
        justify-content: center;
        padding: 1rem;
      }

      &.highlighted {
        color: var(--textColor) !important;
        .group:not(:last-of-type) {
          margin-right: 1rem;
        }
      }

      .group {
        display: flex;
        flex-direction: column;

        & > div {
          display: flex;
        }
      }

      .label {
        font-weight: bold;
        margin-bottom: 1rem;
      }

      .options {
        display: flex;
      }
    }
  }
</style>
